/**
 * PWA Install Prompt Component
 * Shows a banner prompting users to install the app on their device
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // For testing: clear the dismissal flag
    // localStorage.removeItem('pwa-prompt-dismissed');

    // Check if prompt was dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show for 7 days after dismissal
      }
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS-specific prompt after a short delay
    if (iOS && !standalone) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
    }
    
    // For Android/Chrome - show prompt after detecting PWA support
    // Even if beforeinstallprompt hasn't fired yet, show manual instructions
    if (!iOS && !standalone && 'serviceWorker' in navigator) {
      setTimeout(() => {
        // If no install prompt was captured, still show the banner with manual instructions
        if (!deferredPrompt) {
          setShowPrompt(true);
        }
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't show if already installed or prompt shouldn't be shown
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-xl shadow-2xl p-4 border border-blue-700">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="bg-white/10 p-2 rounded-lg">
            <Smartphone className="w-8 h-8" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">Install MDDRC App</h3>
            
            {isIOS ? (
              <div className="text-sm text-blue-100">
                <p className="mb-2">Install this app on your iPhone:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Tap the <span className="inline-block px-1 bg-white/20 rounded">Share</span> button</li>
                  <li>Scroll down and tap <span className="font-medium">"Add to Home Screen"</span></li>
                </ol>
              </div>
            ) : deferredPrompt ? (
              <>
                <p className="text-sm text-blue-100 mb-3">
                  Get quick access from your home screen. Works offline!
                </p>
                <Button
                  onClick={handleInstall}
                  className="bg-white text-blue-900 hover:bg-blue-50 w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install App
                </Button>
              </>
            ) : (
              <div className="text-sm text-blue-100">
                <p className="mb-2">Install this app on your device:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Tap the <span className="inline-block px-1 bg-white/20 rounded">⋮</span> menu (top right)</li>
                  <li>Tap <span className="font-medium">"Install app"</span> or <span className="font-medium">"Add to Home Screen"</span></li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
